/**
 * ProductCardModal — окно товара на каркасе сущности.
 *
 * Открывается из каталога и синхронизировано с URL (?article=PT…): ссылку можно
 * переслать, F5 не закрывает окно. Соседние товары страницы — стрелками «N из M»
 * в шапке, правки защищены от случайной потери. Шапка, цепочка «тематика ›
 * макет › товар › площадки», полоса «Что дальше», колонка, вкладки и меню «Ещё»
 * — общий каркас `EntityModalShell`; здесь только содержимое товара.
 *
 * Пряники, наборы и их заготовки дополняют окно расширением
 * (`extension`): вид, состав, производственные файлы, свои кнопки.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Copy,
  Eye,
  History as HistoryIcon,
  ImagePlus,
  Images,
  Layers,
  Link2,
  ListChecks,
  Package,
  Printer,
  Send,
  Store,
  Trash2,
  FileEdit,
  Factory,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { hasPermission } from '../../../app/access'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { copyToClipboard } from '../../../components/ui/copyToClipboard'
import { EntityChain, type ChainItem, type ChainTone } from '../../../components/ui/EntityChain'
import type { IdMetaItem } from '../../../components/ui/IdMeta'
import { Lightbox } from '../../../components/ui/Lightbox'
import { Modal } from '../../../components/ui/Modal'
import {
  NextStepBar,
  type NextStepAction,
  type NextStepCheck,
} from '../../../components/ui/NextStepBar'
import { UnderlineTabs } from '../../../components/ui/UnderlineTabs'
import {
  EntityModalShell,
  type EntityMoreMenuItem,
} from '../../../components/entity-view/EntityModalShell'
import { useEntityNavigator } from '../../../components/entity-view/navigatorContext'
import { RebuildCardsWizard } from '../../../components/pipeline-regeneration'
import { PhotoResendModal } from '../../../components/marketplace/PhotoResendModal'
import { getMediaUrl } from '../../../utils/mediaUtils'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useAuthStore } from '../../../stores/authStore'
import { useToastStore } from '../../../stores/toastStore'
import { pipelineAPI } from '../../pipeline/api'
import { CardSidebar, type CardSidebarStat } from '../../marketplace-catalog/components/CardSidebar'
import { productsV2API } from '../api'
import { catalogRowPreview, formatPrice, isNoDesignCode, printArtifactInfo } from '../lib'
import { buildPresenceChain, livePlatformCount, useProductPresence } from '../presence'
import type { ProductPresenceEntry, ProductV2 } from '../types'
import type { ManualProductCreatedSummary } from './CreateManualProductModal'
import { useProductHistory } from './ProductHistoryPanel'
import { AttrsTab, doughWeightText, type EditableForm } from './product-window/AttrsTab'
import { MediaTab, type GalleryItem } from './product-window/MediaTab'
import {
  NO_DATA,
  buildVerdict,
  categoryLabel,
  isManualProduct,
  marketplaceIds,
  oneCCheck,
  oneCInfo,
  placesCheck,
  priceCheck,
  priceOf,
  printCheck,
  productImages,
  productLink,
  themeNumberOfProduct,
  validationCheck,
  validationCount,
  visualsCheck,
  pluralRemarks,
  type ProductCheck,
} from './product-window/model'
import { OverviewTab } from './product-window/OverviewTab'
import { PlacesTab } from './product-window/PlacesTab'
import { PrintProductionTab } from './product-window/PrintProductionTab'
import { ProductHistoryPanel } from './ProductHistoryPanel'
import { ProductTags } from './product-window/ProductTags'
import {
  EMPTY_CONTRIBUTION,
  type ProductWindowContribution,
  type ProductWindowExtension,
  type ProductWindowTab,
} from './product-window/types'

export type { ProductWindowExtension, ProductWindowContribution, ProductWindowTab } from './product-window/types'

/**
 * Дополнительная вкладка карточки. Страница, которая открывает карточку,
 * может добавить свою; вкладка с ключом `production` становится «Производством»
 * вместо штатной (так каталог пряников подключает свои файлы), остальные
 * встают перед «Площадками».
 */
export interface ProductCardExtraTab {
  key: string
  label: string
  render: (product: ProductV2) => React.ReactNode
}

const NO_EXTRA_TABS: ProductCardExtraTab[] = []

interface ProductCardModalProps {
  isOpen: boolean
  article: string | null
  /** Артикулы текущей страницы — для навигации «N из M» */
  articles: string[]
  onNavigate: (article: string | null) => void
  onClose: () => void
  onSaved: () => void
  /** Удаление товара; без обработчика пункт «Удалить товар» неактивен с причиной. */
  onDelete?: (product: ProductV2) => void
  categories: Array<{ id: number; description: string }>
  /** Строка из списка: показываем сразу, пока грузится полная карточка */
  initial?: ProductV2 | null
  /** Открыто поверх другого окна (из карточки макета) — встаёт выше него */
  layer?: 'default' | 'nested'
  creationSummary?: ManualProductCreatedSummary | null
  onCreateAnother?: () => void
  /** Дополнительные вкладки; по умолчанию их нет. */
  extraTabs?: ProductCardExtraTab[]
  /** Расширение окна (пряники и наборы). Ссылка обязана быть стабильной. */
  extension?: ProductWindowExtension
  /** Окно открыто навигатором по цепочке: есть куда вернуться (кнопка и Alt+←). */
  canGoBack?: boolean
  onBack?: () => void
}

// Стабильный литерал вместо `[]` инлайн — тот же приём, что и в
// `PimMarketplaceDrafts.tsx`, чтобы селектор `useAuthStore` не считал
// пользователя без прав меняющимся на каждый рендер.
const NO_PERMISSIONS: string[] = []

const useNoContribution = (): ProductWindowContribution => EMPTY_CONTRIBUTION
const NO_EXTENSION: ProductWindowExtension = { useContribution: useNoContribution }

const needRight = (right: string) => `Нужно право ${right}`

const DIRTY_LABELS: Array<[keyof EditableForm, string]> = [
  ['title', 'Наименование'],
  ['category_id', 'Категория'],
  ['barcode', 'Штрихкод'],
  ['dough_weight_g', 'Вес теста'],
]

/** Диалоги поверх вложенного окна должны стоять выше него. */
const Overlays: React.FC<{ nested: boolean; children: React.ReactNode }> = ({ nested, children }) =>
  nested ? <div className="relative z-[100]">{children}</div> : <>{children}</>

const chainToneOf = (tone: 'ok' | 'warn' | 'bad' | 'info' | 'neutral'): ChainTone =>
  tone === 'neutral' ? 'none' : tone

export const ProductCardModal: React.FC<ProductCardModalProps> = ({
  isOpen,
  article,
  articles,
  onNavigate,
  onClose,
  onSaved,
  onDelete,
  categories,
  initial,
  layer = 'default',
  creationSummary,
  onCreateAnother,
  extraTabs = NO_EXTRA_TABS,
  extension = NO_EXTENSION,
  canGoBack,
  onBack,
}) => {
  const permissions = useAuthStore((state) => state.user?.permissions ?? NO_PERMISSIONS)
  const canRedoHeroPhoto = hasPermission('ContentFunnel:UPDATE', permissions)
  const canResendPhotos = hasPermission('Card:UPDATE', permissions)
  const canRunPipeline = hasPermission('Pipeline:UPDATE', permissions)
  const canEditProduct = hasPermission('Product:UPDATE', permissions)
  const navigate = useNavigate()
  const entityNav = useEntityNavigator()
  const showToast = useToastStore((state) => state.showToast)
  const [tab, setTab] = useState<string>('overview')
  const [product, setProduct] = useState<ProductV2 | null>(initial || null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<EditableForm>({ title: '', barcode: '', category_id: '', dough_weight_g: '' })
  const [baseline, setBaseline] = useState<EditableForm>({ title: '', barcode: '', category_id: '', dough_weight_g: '' })
  const [guardOpen, setGuardOpen] = useState(false)
  const pendingAction = useRef<(() => void) | null>(null)
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [photoResendOpen, setPhotoResendOpen] = useState(false)
  const [rebuildOpen, setRebuildOpen] = useState(false)
  const [regenerateConfirm, setRegenerateConfirm] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [resolvedFailures, setResolvedFailures] = useState<Set<string>>(new Set())
  const [retryingFailure, setRetryingFailure] = useState<string | null>(null)
  const [retryFailureErrors, setRetryFailureErrors] = useState<Record<string, string>>({})

  const presence = useProductPresence(isOpen ? article : null)
  const history = useProductHistory(isOpen ? article : null)

  useEffect(() => {
    setResolvedFailures(new Set())
    setRetryingFailure(null)
    setRetryFailureErrors({})
  }, [creationSummary?.article])

  const dirtyLabels = useMemo(
    () => DIRTY_LABELS.filter(([key]) => form[key] !== baseline[key]).map(([, label]) => label),
    [form, baseline],
  )
  const dirty = dirtyLabels.length > 0

  // ── Загрузка карточки ──
  useEffect(() => {
    if (!isOpen || !article) return
    let cancelled = false

    setLoading(true)
    setError(null)
    setTab('overview')
    if (initial) setProduct(initial)

    productsV2API
      .getProduct(article)
      .then((data: any) => {
        if (cancelled) return
        // Список знает про карточки МП и связь с Ozon, одиночный эндпоинт — нет
        const merged: ProductV2 = {
          ...(initial || {}),
          ...data,
          cards: data.cards ?? initial?.cards ?? [],
          ozon_offer_id: data.ozon_offer_id ?? initial?.ozon_offer_id ?? null,
          price: data.price ?? initial?.price ?? data.category?.price ?? null,
        }
        setProduct(merged)
        const next: EditableForm = {
          title: merged.title || '',
          barcode: merged.barcode || '',
          category_id: merged.category_id ? String(merged.category_id) : '',
          dough_weight_g: doughWeightText(merged.dough_weight_g),
        }
        setForm(next)
        setBaseline(next)
      })
      .catch((e: any) => {
        if (cancelled) return
        const message = e?.response?.data?.error?.message || e?.message || ''
        setError(e?.response?.status === 404 || /product not found/i.test(message)
          ? 'Товар ' + article + ' не найден'
          : 'Не удалось загрузить товар. Попробуйте открыть окно ещё раз.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, article])

  const save = useCallback(async (): Promise<boolean> => {
    if (!product || !dirty) return true
    setSaving(true)
    try {
      await productsV2API.updateProduct(product.article, {
        title: form.title,
        barcode: form.barcode || null,
        category_id: form.category_id ? Number(form.category_id) : null,
        ...(form.dough_weight_g !== baseline.dough_weight_g
          ? { dough_weight_g: form.dough_weight_g.trim() === '' ? null : Number(form.dough_weight_g.replace(',', '.')) }
          : {}),
      })
      setBaseline(form)
      onSaved()
      return true
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Не удалось сохранить')
      return false
    } finally {
      setSaving(false)
    }
  }, [product, dirty, form, baseline, onSaved])

  /** Любой уход из окна с несохранёнными правками сначала спрашивает */
  const guard = useCallback(
    (action: () => void) => {
      if (!dirty) {
        action()
        return
      }
      pendingAction.current = action
      setGuardOpen(true)
    },
    [dirty],
  )

  // ── Расширение окна (пряники, наборы) ──
  const contribution = extension.useContribution(product, {
    setTab,
    guard,
    presence: presence.entries,
    article,
  })

  // ── Изображения ──
  const productPhotoItems: GalleryItem[] = productImages(product ?? ({} as ProductV2)).map((url) => ({
    key: `product:${url}`,
    src: getMediaUrl(url),
    label: `${product?.article} · фото товара`,
    alt: `Фото товара ${product?.article}`,
  }))
  // Превью макета — файл печати, на площадки оно не уходит. В одной сетке с
  // кадрами воронки его принимали за выгруженное фото, поэтому блок отдельный.
  const layoutPreviewItems: GalleryItem[] = (isNoDesignCode(product?.design?.design_code) || isNoDesignCode(product?.design_id) ? [] : product?.design?.media || [])
    .filter((media) => media.type !== 'pdf')
    .map((media) => ({
      key: `design:${media.id}`,
      src: getMediaUrl(media, product?.design?.design_code),
      label: `${product?.article} · превью макета #${product?.design?.design_code}`,
      alt: `Превью макета ${product?.design?.design_code}`,
    }))
  const preview = product && !isManualProduct(product) && !isNoDesignCode(product.design_id) && !isNoDesignCode(product.design?.design_code)
    ? catalogRowPreview(product)
    : null
  const heroUrl = contribution.heroUrl ?? (preview?.media ? getMediaUrl(preview.media.url, product?.design?.design_code) : null)
  const heroLabel = contribution.heroUrl ? 'Превью производства' : preview?.sourceLabel
  // Один порядок для колонки, медиа и лайтбокса: одинаковый источник не дублируется.
  const lightboxItems = [
    ...(heroUrl ? [{ src: heroUrl, label: product?.article + ' · ' + heroLabel }] : []),
    ...productPhotoItems.map(({ src, label }) => ({ src, label })),
    ...layoutPreviewItems.map(({ src, label }) => ({ src, label })),
  ].filter((item, index, items) => items.findIndex((other) => other.src === item.src) === index)
  const openGallerySource = (src: string | undefined) => {
    const index = lightboxItems.findIndex((item) => item.src === src)
    if (index >= 0) setLightbox(index)
  }

  const manual = isManualProduct(product)
  const rawDesignCode = product?.design?.design_code ?? null
  // NODESIGN — служебный код «макета нет», а не номер макета: ни ссылки, ни «#NODESIGN».
  const designCode = isNoDesignCode(rawDesignCode) || isNoDesignCode(product?.design_id) ? null : rawDesignCode
  const withoutDesign = manual || isNoDesignCode(rawDesignCode) || isNoDesignCode(product?.design_id)
  const activeCreationFailures = (creationSummary?.failures || []).filter(
    (failure) => !resolvedFailures.has(failure.id),
  )
  const creationNotices = (creationSummary?.issues || []).filter(
    (issue) => !(creationSummary?.failures || []).some((failure) => issue.includes(failure.message)),
  )
  const creationSiteStatus =
    creationSummary?.siteCard === 'created'
      ? 'заведена'
      : creationSummary?.siteCard === 'failed' && resolvedFailures.has('site-card')
        ? 'заведена после повтора'
        : creationSummary?.siteCard === 'failed'
          ? 'ошибка'
          : 'пропущена'

  const openDesign =
    designCode !== null && !manual && entityNav.available
      ? () => guard(() => entityNav.open({ kind: 'design', code: designCode }))
      : undefined

  const openPresenceEntry = useCallback(
    (entry: ProductPresenceEntry) => {
      if (!product || !entityNav.available) return
      // Та же логика, что у звена цепочки: зеркало → карточка витрины, иначе черновик.
      const chain = buildPresenceChain({
        entries: [entry],
        article: product.article,
        open: entityNav.open,
        guard,
      })
      chain.items.find((item) => item.key === entry.marketplace)?.onClick?.()
    },
    [product, entityNav, guard],
  )

  const goTab = useCallback((key: string) => setTab(key), [])

  // ── Проверки и вывод ──
  const handlers = { goTab }
  const baseChecks: ProductCheck[] = product
    ? [
        oneCCheck(product, handlers),
        printCheck(product, handlers),
        visualsCheck(product, handlers),
        placesCheck(presence.entries, presence.error, handlers),
        validationCheck(product, handlers),
        priceCheck(product, handlers),
      ]
    : []
  const checks: NextStepCheck[] = baseChecks.map(
    (check) => contribution.checks?.find((extra) => extra.key === check.key) ?? check,
  )
  for (const extra of contribution.checks ?? []) {
    if (!checks.some((check) => check.key === extra.key)) checks.push(extra)
  }
  // Проблемы расширения: чип с тоном bad/warn и своей формулировкой
  const verdictInputs: ProductCheck[] = checks.map((check) => {
    const base = baseChecks.find((item) => item.key === check.key)
    const problem = (check as ProductCheck).problem
    // Чип расширения заменил штатный: его формулировку берём, если она есть; иначе проблемы нет.
    return contribution.checks?.some((extra) => extra.key === check.key)
      ? { ...check, problem }
      : { ...check, problem: base?.problem }
  })
  const verdict = buildVerdict(verdictInputs, !product || loading || (presence.entries === null && !presence.error))

  // ── Действия полосы ──
  const printMissing =
    product && !manual && designCode !== null && printArtifactInfo(product).variant !== 'green' && product.print_artifact?.status !== 'generating' && product.print_artifact?.status !== 'pending'
  const noCardsAnywhere =
    presence.entries !== null &&
    presence.entries.every((entry) => entry.state === 'none' || entry.state === 'archived')
  const inOneC = product?.one_c_status === 'synced'

  const baseActions: NextStepAction[] = []
  if (product && printMissing && !contribution.hideActions?.includes('print')) {
    baseActions.push({
      key: 'print',
      label: 'Перегенерировать файл печати',
      icon: <Printer className="h-4 w-4" />,
      onClick: () => setRegenerateConfirm(true),
      disabledReason: canRunPipeline ? undefined : needRight('Pipeline:UPDATE'),
    })
  }
  if (
    product &&
    !manual &&
    designCode !== null &&
    noCardsAnywhere &&
    inOneC &&
    !contribution.hideActions?.includes('create-cards')
  ) {
    baseActions.push({
      key: 'create-cards',
      label: 'Создать карточки',
      icon: <Layers className="h-4 w-4" />,
      onClick: () => setRebuildOpen(true),
      disabledReason: canRunPipeline ? undefined : needRight('Pipeline:UPDATE'),
    })
  }
  const issueTab: Record<string, { tab: string; label: string }> = {
    places: { tab: 'places', label: 'Проверить отправку' },
    validation: { tab: 'attrs', label: 'Проверить данные' },
    onec: { tab: 'attrs', label: 'Проверить синхронизацию' },
    price: { tab: 'overview', label: 'Посмотреть данные категории' },
    composition: { tab: 'composition', label: 'Проверить состав' },
  }
  const destination = verdict.problemKey ? issueTab[verdict.problemKey] : undefined
  const selectedCheck = verdictInputs.find((check) => check.key === verdict.problemKey)
  const relevantAction: NextStepAction | undefined =
    verdict.problemKey === 'visuals' ? contribution.actions?.find((action) => action.key === 'make-visuals')
      : verdict.problemKey === 'print' ? baseActions.find((action) => action.key === 'print')
      : verdict.problemKey === 'places' && noCardsAnywhere ? contribution.actions?.find((action) => action.key === 'open-drafts') ?? baseActions.find((action) => action.key === 'create-cards')
        : destination ? { key: 'inspect-' + verdict.problemKey, label: destination.label, onClick: () => setTab(destination.tab) }
          : undefined
  const nextActions: NextStepAction[] = verdict.tone === 'neutral' ? []
    : relevantAction ? [{ ...relevantAction, variant: 'primary' }]
      : verdict.problemKey ? selectedCheck?.onClick ? [{ key: 'inspect-' + verdict.problemKey, label: 'Проверить: ' + selectedCheck.label, onClick: selectedCheck.onClick, variant: 'primary' }] : []
        : (contribution.actions ?? []).slice(0, 2).map((action, index) => ({ ...action, variant: index === 0 ? 'primary' : 'secondary' }))

  // ── Шапка ──
  const info = product ? oneCInfo(product) : null
  const remarks = product ? validationCount(product) : null
  const badges = product ? (
    <>
      {info && info.tone !== 'none' && <Badge variant={info.variant}>{info.label}</Badge>}
      {remarks !== null && remarks > 0 && (
        <span title={product.validation_issues?.join('\n')}>
          <Badge variant="yellow">{pluralRemarks(remarks)}</Badge>
        </span>
      )}
      {withoutDesign && <Badge variant="gray">Без макета</Badge>}
    </>
  ) : undefined

  const ids = product ? marketplaceIds(product) : { wb: null, ozon: null, ym: null }
  const metaItems: IdMetaItem[] = product
    ? [
        { label: 'Штрихкод', value: product.barcode || form.barcode || null, mono: true, copy: true },
        { label: 'Категория', value: categoryLabel(product, categories.find((item) => String(item.id) === form.category_id)?.description) },
        {
          label: 'Макет',
          value: withoutDesign ? 'Без макета' : designCode !== null ? `#${designCode}` : null,
          mono: !withoutDesign,
          copy: !withoutDesign,
          onClick: openDesign,
        },
        { label: 'WB nmID', value: ids.wb, mono: true, copy: true },
        { label: 'OZON offer', value: ids.ozon, mono: true, copy: true },
        { label: 'ЯМ SKU', value: ids.ym, mono: true, copy: true },
        ...(contribution.meta ?? []),
      ]
    : []

  // ── Цепочка ──
  const themeNumber = product ? themeNumberOfProduct(product) : null
  const chainItems: ChainItem[] = product
    ? [
        ...(contribution.chainBefore ?? []),
        ...(themeNumber !== null
          ? [{ key: 'theme', label: 'Тематика', sub: `№ ${themeNumber}`, tone: 'ok' as const, title: 'Отдельного окна тематики пока нет' }]
          : []),
        manual || designCode === null
          ? { key: 'design', label: withoutDesign ? 'Без макета' : 'Макет', tone: 'none' as const, disabled: true, title: 'у товара нет макета' }
          : {
              key: 'design',
              label: 'Макет',
              sub: `#${designCode}`,
              tone: 'ok' as const,
              onClick: openDesign,
            },
        { key: 'product', label: 'Товар', sub: product.article, current: true, tone: chainToneOf(verdict.tone) },
        buildPresenceChain({
          entries: presence.entries,
          article: product.article,
          open: entityNav.available ? entityNav.open : undefined,
          guard,
        }),
      ]
    : []

  // ── Меню «Ещё» ──
  const copyText = async (text: string, what: string) => {
    const ok = await copyToClipboard(text)
    if (ok) showToast(`Скопировано: ${what}`, 'success')
    else showToast('Не удалось скопировать', 'error')
  }
  const moreMenu: EntityMoreMenuItem[] = product
    ? [
        {
          key: 'copy-article',
          label: 'Скопировать артикул',
          icon: <Copy className="h-4 w-4" />,
          hotkey: 'C',
          onClick: () => void copyText(product.article, 'артикул'),
        },
        {
          key: 'copy-link',
          label: 'Скопировать ссылку',
          icon: <Link2 className="h-4 w-4" />,
          hotkey: 'Shift+C',
          onClick: () => void copyText(productLink(product.article), 'ссылка'),
        },
        { divider: true },
        {
          key: 'redo-hero',
          label: 'Переделать главное фото',
          icon: <ImagePlus className="h-4 w-4" />,
          disabledReason: canRedoHeroPhoto ? undefined : needRight('ContentFunnel:UPDATE'),
          onClick: () =>
            guard(() =>
              navigate(`/content-funnel-studio/new?mode=hero&article=${encodeURIComponent(product.article)}`),
            ),
        },
        {
          key: 'resend-photo',
          label: 'Переотправить фото…',
          icon: <Send className="h-4 w-4" />,
          disabledReason: canResendPhotos ? undefined : needRight('Card:UPDATE'),
          onClick: () => setPhotoResendOpen(true),
        },
        ...(openDesign
          ? [{
              key: 'open-design',
              label: 'Открыть макет',
              icon: <FileEdit className="h-4 w-4" />,
              onClick: openDesign,
            }]
          : []),
        ...(contribution.moreMenu ?? []),
        {
          key: 'delete',
          label: 'Удалить товар',
          icon: <Trash2 className="h-4 w-4" />,
          danger: true,
          disabledReason: onDelete ? undefined : 'Удаление товара доступно на странице «Товары»',
          onClick: () => {
            if (onDelete) guard(() => onDelete(product))
          },
        },
      ]
    : []

  // ── Вкладки ──
  const printTabAttention: 'warn' | 'bad' | undefined =
    product && !manual && product.print_artifact?.status === 'failed'
      ? 'bad'
      : product && !manual && designCode !== null && !product.print_artifact
        ? 'bad'
        : undefined
  const extraProduction = extraTabs.find((extra) => extra.key === 'production')
  const productionExtension = contribution.tabs?.find((extra) => extra.key === 'production')
  const compositionTab = contribution.tabs?.find((extra) => extra.key === 'composition')
  const otherExtension = (contribution.tabs ?? []).filter(
    (extra) => extra.key !== 'production' && extra.key !== 'composition',
  )
  const otherExtra = extraTabs.filter((extra) => extra.key !== 'production')
  const regenerateReason = canRunPipeline ? undefined : needRight('Pipeline:UPDATE')

  const liveCount = livePlatformCount(presence.entries)
  type TabDef = ProductWindowTab
  const tabDefs: TabDef[] = product
    ? [
        {
          key: 'overview',
          label: 'Обзор',
          icon: <Eye className="h-4 w-4" />,
          render: () => (
            <OverviewTab
              product={product}
              presence={presence}
              history={history}
              onOpenEntry={entityNav.available ? openPresenceEntry : undefined}
              onShowHistory={() => setTab('history')}
            />
          ),
        },
        ...(compositionTab ? [compositionTab] : []),
        {
          key: 'attrs',
          label: 'Атрибуты',
          icon: <ListChecks className="h-4 w-4" />,
          attention: remarks !== null && remarks > 0 ? ('warn' as const) : undefined,
          render: () => (
            <AttrsTab
              product={product}
              form={form}
              onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
              categories={categories}
              onOpenDesign={openDesign}
            />
          ),
        },
        {
          key: 'media',
          label: 'Медиа',
          icon: <Images className="h-4 w-4" />,
          count: productImages(product).length || undefined,
          render: () => (
            <MediaTab
              product={product}
              productPhotos={productPhotoItems}
              layoutPreviews={layoutPreviewItems}
              onOpenImage={(index) => openGallerySource([...productPhotoItems, ...layoutPreviewItems][index]?.src)}
              onChanged={onSaved}
              onResendPhotos={canResendPhotos ? () => setPhotoResendOpen(true) : undefined}
            />
          ),
        },
        productionExtension
          ? { ...productionExtension, icon: productionExtension.icon ?? <Factory className="h-4 w-4" /> }
          : extraProduction
            ? {
                key: 'production',
                label: extraProduction.label,
                icon: <Factory className="h-4 w-4" />,
                render: extraProduction.render,
              }
            : {
                key: 'production',
                label: 'Производство',
                icon: <Factory className="h-4 w-4" />,
                attention: printTabAttention,
                render: () => (
                  <PrintProductionTab
                    product={product}
                    onRegenerate={!manual && designCode !== null ? () => setRegenerateConfirm(true) : undefined}
                    regenerateDisabledReason={regenerateReason}
                  />
                ),
              },
        ...otherExtension,
        ...otherExtra.map((extra) => ({ key: extra.key, label: extra.label, render: extra.render })),
        {
          key: 'places',
          label: 'Площадки',
          icon: <Store className="h-4 w-4" />,
          count: liveCount || undefined,
          attention: presence.entries?.some((entry) => entry.state === 'error') ? ('bad' as const) : undefined,
          render: () => (
            <PlacesTab
              product={product}
              presence={presence}
              onOpenEntry={entityNav.available ? openPresenceEntry : undefined}
            />
          ),
        },
        {
          key: 'history',
          label: 'История',
          icon: <HistoryIcon className="h-4 w-4" />,
          count: history.items?.length || undefined,
          render: () => <ProductHistoryPanel article={product.article} data={history} />,
        },
      ]
    : []
  const activeTab = tabDefs.find((item) => item.key === tab) ?? tabDefs[0]

  // ── Колонка ──
  const noData = (label: string, note: string): CardSidebarStat => ({ label, value: NO_DATA, tone: 'dim', note })
  const price = product ? priceOf(product) : null
  const imageCount = product ? productImages(product).length : 0
  const sidebarStats: CardSidebarStat[] = product
    ? [
        price !== null
          ? { label: 'Цена категории', value: formatPrice(price) }
          : noData('Цена категории', 'у категории не задана'),
        liveCount === null
          ? noData('На витрине', presence.error ? 'присутствие не загрузилось' : 'загружается')
          : { label: 'На витрине', value: liveCount, note: 'из 4 площадок' },
        imageCount > 0
          ? { label: 'Визуалов', value: imageCount, note: 'изображений товара' }
          : noData('Визуалов', 'изображений товара нет'),
        ...(contribution.stats ?? []),
      ]
    : []
  const sidebarImages = [heroUrl, ...productPhotoItems.map((item) => item.src)].filter(
    (src): src is string => Boolean(src),
  ).filter((src, index, sources) => sources.indexOf(src) === index)
  const [activeImage, setActiveImage] = useState(0)
  useEffect(() => setActiveImage(0), [article])

  const sidebar = product ? (
    <CardSidebar
      aspect="square"
      images={sidebarImages}
      activeImage={Math.min(activeImage, Math.max(sidebarImages.length - 1, 0))}
      onActiveImage={setActiveImage}
      onOpenImage={(index) => openGallerySource(sidebarImages[index])}
      onShowAll={() => setTab('media')}
      emptyImageText="У товара нет изображений"
      imageCaption={sidebarImages[Math.min(activeImage, Math.max(sidebarImages.length - 1, 0))] === heroUrl ? heroLabel : "Визуал карточки"}
      renderImage={(src, alt, className) => (
        <AuthorizedMediaImage src={src} alt={alt} thumbnailWidth={640} className={`${className} bg-white`} />
      )}
      stats={sidebarStats}
      originsTitle="Идентификаторы"
      origins={[
        { label: 'Артикул', value: product.article, mono: true, copy: true },
        ...(designCode !== null && !manual
          ? [{ label: 'Макет', value: `#${designCode}`, mono: true, onClick: openDesign, href: undefined }]
          : withoutDesign ? [{ label: 'Макет', value: 'Без макета' }] : []),
        { label: 'Штрихкод', value: product.barcode || 'не задан', mono: Boolean(product.barcode), copy: Boolean(product.barcode) },
        ...(ids.ozon ? [{ label: 'OZON offer', value: ids.ozon, mono: true, copy: true }] : []),
        ...(ids.wb ? [{ label: 'WB nmID', value: ids.wb, mono: true, copy: true }] : []),
        ...(ids.ym ? [{ label: 'ЯМ SKU', value: ids.ym, mono: true, copy: true }] : []),
      ]}
      footer={
        <ProductTags
          article={product.article}
          tags={product.tags ?? []}
          readOnly={!canEditProduct}
          onChange={(tags) => setProduct((current) => (current ? { ...current, tags } : current))}
        />
      }
    />
  ) : undefined

  // ── Баннеры: плашка создания товара и ошибка загрузки ──
  const banner =
    creationSummary || error ? (
      <>
        {creationSummary && (
          <div
            className={`shrink-0 border-b px-5 py-2 text-sm ${activeCreationFailures.length || creationNotices.length ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300' : 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-300'}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span>
                Товар <span className="font-mono">{creationSummary.article}</span> создан. Фото: {creationSummary.photos} · Карточка сайта: {creationSiteStatus} · 1С: в очереди
              </span>
              {onCreateAnother && (
                <Button size="sm" variant="secondary" onClick={onCreateAnother}>Создать ещё один товар</Button>
              )}
            </div>
            {activeCreationFailures.length > 0 && (
              <ul className="mt-2 space-y-2 text-xs">
                {activeCreationFailures.map((failure) => (
                  <li key={failure.id} className="rounded border border-amber-200 bg-white/60 px-2 py-1.5 dark:border-amber-900 dark:bg-zinc-900/40">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex-1"><b>{failure.label}:</b> {failure.message}</span>
                      <a className="underline" href={`/products?article=${encodeURIComponent(creationSummary.article)}`}>Карточка товара</a>
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={retryingFailure === failure.id}
                        disabled={Boolean(retryingFailure)}
                        onClick={async () => {
                          setRetryingFailure(failure.id)
                          setRetryFailureErrors((current) => ({ ...current, [failure.id]: '' }))
                          try {
                            await failure.retry()
                            setResolvedFailures((current) => new Set(current).add(failure.id))
                            onSaved()
                          } catch (retryError: any) {
                            setRetryFailureErrors((current) => ({
                              ...current,
                              [failure.id]: retryError?.response?.data?.error?.message || retryError?.message || 'Повтор не удался',
                            }))
                          } finally {
                            setRetryingFailure(null)
                          }
                        }}
                      >
                        Повторить
                      </Button>
                    </div>
                    {retryFailureErrors[failure.id] && <p className="mt-1 text-red-600 dark:text-red-400">{retryFailureErrors[failure.id]}</p>}
                  </li>
                ))}
              </ul>
            )}
            {creationNotices.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs">
                {creationNotices.map((notice, noticeIndex) => <li key={`${notice}:${noticeIndex}`}>{notice}</li>)}
              </ul>
            )}
          </div>
        )}
        {error && (
          <div className="shrink-0 border-b border-red-200 bg-red-50 px-5 py-2.5 text-sm text-red-700 dark:border-red-950 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        )}
      </>
    ) : undefined

  const regenerate = async () => {
    if (designCode === null) return
    setRegenerating(true)
    try {
      const started = await pipelineAPI.regenerateProductionFiles(String(designCode))
      const categoriesCount = Array.isArray(started?.production_jobs) ? started.production_jobs.length : 0
      showToast(
        categoriesCount
          ? `Файлы для печати создаются заново: категорий ${categoriesCount}`
          : 'Создание файлов для печати запущено',
        'success',
      )
      setRegenerateConfirm(false)
      onSaved()
    } catch (e: any) {
      showToast(e?.response?.data?.error?.message || e?.message || 'Не удалось запустить перегенерацию', 'error')
    } finally {
      setRegenerating(false)
    }
  }

  const keys = articles
  const overlaysOpen = guardOpen || photoResendOpen || rebuildOpen || regenerateConfirm

  return (
    <>
      <EntityModalShell
        isOpen={isOpen}
        ariaLabel={product ? `Товар ${product.article}` : 'Карточка товара'}
        layer={layer}
        kind={{
          label: contribution.kind?.label ?? 'Товар',
          icon: contribution.kind?.icon ?? Package,
        }}
        primaryId={
          product
            ? { value: product.article, label: 'артикул', link: productLink(product.article) }
            : article
              ? { value: article, label: 'артикул' }
              : undefined
        }
        badges={badges}
        title={product ? form.title.trim() || product.title || product.article : undefined}
        meta={metaItems}
        moreMenu={moreMenu}
        keys={keys}
        activeKey={article}
        onNavigate={(key) => guard(() => onNavigate(key))}
        onClose={() => guard(onClose)}
        canGoBack={canGoBack}
        onBack={onBack ? () => guard(onBack) : undefined}
        suspendHotkeys={overlaysOpen}
        loading={!product && loading}
        chain={product ? <EntityChain items={chainItems} onBack={canGoBack && onBack ? () => guard(onBack) : undefined} /> : undefined}
        banner={banner}
        nextStep={
          product ? (
            <NextStepBar
              tone={verdict.tone}
              title={verdict.title}
              hint={verdict.hint}
              actions={nextActions}
              checks={checks}
            />
          ) : undefined
        }
        sidebar={sidebar}
        tabsHotkeys={{ keys: tabDefs.map((item) => item.key), onSelect: setTab }}
        tabs={
          product ? (
            <UnderlineTabs<string>
              tabs={tabDefs.map((item) => ({
                key: item.key,
                label: item.label,
                icon: item.icon,
                count: item.count,
                attention: item.attention,
              }))}
              active={activeTab?.key ?? 'overview'}
              onChange={setTab}
              ariaLabel="Разделы окна товара"
            />
          ) : undefined
        }
        onSave={() => {
          if (dirty && !saving) void save()
        }}
        footer={
          <>
            {dirty ? (
              <span className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                Изменено: {dirtyLabels.join(', ')}
              </span>
            ) : (
              <span className="text-xs text-gray-500 dark:text-zinc-400">Сохранено</span>
            )}
            <span className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => guard(onClose)}>
                Закрыть
              </Button>
              <span title={dirty ? 'Ctrl+S' : 'Нет несохранённых правок'}>
                <Button size="sm" disabled={!dirty} loading={saving} onClick={() => void save()}>
                  Сохранить
                </Button>
              </span>
            </span>
          </>
        }
      >
        {product && activeTab ? <React.Fragment key={activeTab.key}>{activeTab.render(product)}</React.Fragment> : null}
      </EntityModalShell>

      <Overlays nested={layer === 'nested'}>
        <Lightbox
          isOpen={lightbox !== null}
          items={lightboxItems}
          index={lightbox ?? 0}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
        />

        {product && (
          <PhotoResendModal
            isOpen={photoResendOpen}
            onClose={() => setPhotoResendOpen(false)}
            input={{ articles: [product.article] }}
          />
        )}

        {rebuildOpen && product && designCode !== null && (
          <RebuildCardsWizard
            isOpen
            designCodes={[String(designCode)]}
            articles={[product.article]}
            withoutDesignCount={0}
            onClose={() => setRebuildOpen(false)}
            onLaunched={() => {
              presence.reload()
              onSaved()
            }}
          />
        )}

        <ConfirmDialog
          isOpen={regenerateConfirm}
          layer={layer}
          loading={regenerating}
          title="Перегенерировать файлы для печати?"
          confirmLabel="Перегенерировать"
          onClose={() => {
            if (!regenerating) setRegenerateConfirm(false)
          }}
          onConfirm={() => void regenerate()}
          message={
            <p>
              Файлы печати всех категорий макета #{designCode} будут созданы заново и перезапишут текущие в
              хранилище макетов. Визуалы, карточки маркетплейсов и стадия макета останутся как есть.
            </p>
          }
        />

        {contribution.overlays}

        {/* Защита несохранённых правок */}
        <Modal
          isOpen={guardOpen}
          onClose={() => setGuardOpen(false)}
          layer={layer}
          title="Несохранённые изменения"
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setGuardOpen(false)}>
                Остаться
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setGuardOpen(false)
                  setForm(baseline)
                  pendingAction.current?.()
                  pendingAction.current = null
                }}
              >
                Уйти без сохранения
              </Button>
              <Button
                loading={saving}
                onClick={async () => {
                  if (await save()) {
                    setGuardOpen(false)
                    pendingAction.current?.()
                    pendingAction.current = null
                  }
                }}
              >
                Сохранить и уйти
              </Button>
            </>
          }
        >
          <p className="text-sm text-gray-700 dark:text-zinc-200">
            В карточке есть правки, которые ещё не сохранены.
          </p>
        </Modal>
      </Overlays>
    </>
  )
}

export default ProductCardModal
